import {
  ColorType,
  createChart,
  LineData,
  WhitespaceData,
} from "lightweight-charts";
import { useEffect, useRef } from "react";
import { DataPoint } from "./types";

interface Props {
  data: DataPoint[];
  backgroundColor?: string;
  textColor?: string;
  barColor?: string;
  showTime?: boolean;
  formatValue?: (value: number) => string;
  priceLine?: {
    price: number;
    color: string;
  };
}

const Histogram = ({
  data,
  backgroundColor = "transparent",
  textColor = "#999",
  barColor = "#eee",
  showTime,
  formatValue,
  priceLine,
}: Props) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!chartContainerRef.current) return;

    const handleResize = () => {
      chart.applyOptions({ width: chartContainerRef?.current?.clientWidth });
    };

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: backgroundColor },
        textColor,
      },
      grid: {
        vertLines: {
          color: "#222",
        },
        horzLines: {
          color: "#222",
        },
      },
      width: chartContainerRef.current.clientWidth,
      // take container height or default to 300
      height: chartContainerRef.current.clientHeight || 300,
      // show or hide time based on prop
      timeScale: showTime
        ? {
            timeVisible: true,
            secondsVisible: false,
          }
        : undefined,
      localization: {
        priceFormatter:
          formatValue ||
          Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
          }).format,
      },
    });
    chart.timeScale().fitContent();

    const addLine = (data: (LineData | WhitespaceData)[], color: string) => {
      const series = chart.addHistogramSeries({
        color,
        priceLineVisible: false,
      });
      series.setData(data);
      return series;
    };
    const series = addLine(data as DataPoint[], barColor);
    // add price line if provided
    if (priceLine) {
      series.createPriceLine({
        price: priceLine.price,
        color: priceLine.color,
        lineWidth: 2,
        lineStyle: 2,
        axisLabelVisible: false,
      });
    }

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.remove();
    };
  }, [data, backgroundColor, textColor, barColor, showTime]);

  return <div style={{ height: "100%" }} ref={chartContainerRef} />;
};

export default Histogram;
